import type { ApiClient } from "./client";
import type { RequestConfig } from "./types";
import { ApiError } from "./types";

export const tokenErrorCodes = [
  "invalid_token",
  "verifying_token_error",
  "token_not_found",
  "permissions_changed",
];

// 401 codes that are NOT session expiration/invalidation - e.g. failure to
// authenticate with an external provider on our behalf (POST /tts/generate
// with a bad upstream credential). Refreshing the session token does nothing
// for these and can even burn a still-valid session via the refresh burst.
// Extend this list as new "not-session" codes show up on the server.
export const nonSessionErrorCodes = ["invalid_api_key", "provider_auth_failed"];

interface HandlerOptions {
  logoutHandler: () => void;
  refreshTokenHandler: () => Promise<string>;
}

export function handleAuth(api: ApiClient, options: HandlerOptions) {
  let isRefreshing = false;

  const failedQueue: Array<{
    resolve: (value: unknown) => void;
    reject: (reason?: any) => void;
  }> = [];

  function processQueue(error: Error | null, token: string | null = null) {
    for (const entry of failedQueue) {
      if (error) {
        entry.reject(error);
      } else {
        entry.resolve(token);
      }
    }
    failedQueue.length = 0;
  }

  // Proactive token refresh: decode JWT exp and refresh before it expires
  function getTokenExpiry(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleProactiveRefresh(token: string) {
    if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);

    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    // Refresh 60 seconds before expiry
    const refreshAt = expiry - Date.now() - 60_000;
    if (refreshAt <= 0) return;

    proactiveRefreshTimer = setTimeout(async () => {
      try {
        const newToken = await options.refreshTokenHandler();
        api.defaults.headers["Authorization"] = `Bearer ${newToken}`;
        scheduleProactiveRefresh(newToken);
      } catch {
        // Proactive refresh failed - will be handled reactively on next 401
      }
    }, refreshAt);
  }

  function clearProactiveRefresh() {
    if (proactiveRefreshTimer) {
      clearTimeout(proactiveRefreshTimer);
      proactiveRefreshTimer = null;
    }
  }

  // Register error handler for 401/token errors
  api.useErrorHandler(async (error: ApiError, config: RequestConfig) => {
    // Skip refresh/switch endpoints to prevent infinite loops
    if (config.url === "/refresh-session" || config.url === "/switch-organization") return undefined;

    const code = error.response?.data?.error?.code;

    // Handle suspended/inactive users
    if (["user_suspended", "user_inactive"].includes(code)) {
      if (!config.url?.includes("login")) {
        options.logoutHandler();
      }
      throw error;
    }

    // Check if this is a token error. Conservative: a 401 without a code, or
    // with a code in tokenErrorCodes, is still treated as session expiration.
    // Only a 401 whose code is recognizably unrelated to the session (see
    // nonSessionErrorCodes) is excluded.
    const isTokenError =
      (error.response?.status === 401 && !nonSessionErrorCodes.includes(code)) ||
      tokenErrorCodes.includes(code);

    if (!isTokenError) return undefined;

    // Already went through one refresh+retry cycle for this request and it
    // failed again - do not refresh/retry a second time. Returning undefined
    // lets executeFetch throw apiError so the caller's await settles and the
    // error toast can show, instead of recursing into api(retryConfig) forever.
    if (config._retry) return undefined;

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise<any>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (token) {
          const retryConfig = {
            ...config,
            _retry: true,
            headers: { ...config.headers, Authorization: `Bearer ${token}` },
          };
          return api(retryConfig);
        }
        throw new Error("Token is undefined");
      });
      // BUG FIX: The original code had .catch((err) => { Promise.reject(err) })
      // which swallowed errors. The promise chain now propagates rejections correctly.
    }

    // First request to fail - perform refresh
    config._retry = true;
    isRefreshing = true;

    try {
      const token = await options.refreshTokenHandler();

      api.defaults.headers["Authorization"] = `Bearer ${token}`;
      processQueue(null, token);
      isRefreshing = false;
      scheduleProactiveRefresh(token);

      // Retry original request with new token
      const retryConfig = {
        ...config,
        _retry: true,
        headers: { ...config.headers, Authorization: `Bearer ${token}` },
      };
      return api(retryConfig);
    } catch (refreshError) {
      processQueue(refreshError as Error, null);
      isRefreshing = false;
      options.logoutHandler();
      throw refreshError;
    }
  });

  return { scheduleProactiveRefresh, clearProactiveRefresh };
}
